import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, Input, Table, StatusPill, EmptyState,
} from '../../../design-system';
import { money, day, dayTime } from '../../../lib/tallyBooks';
import { useHiddenColumns, applyHidden } from '../../../lib/tableColumns';
import ColumnPicker from './ColumnPicker';
import '../../styles/TallyPanel.css';

// One ledger's whole account, as its own screen.
//
// ⚠️ A SCREEN, NOT A MODAL, AND THE TABLE IS WHY. A ledger statement is
// nineteen columns wide and runs to thousands of rows; a dialog capped at 780px
// turns that into a letterbox with two scrollbars, and the reader spends the
// whole time fighting the container instead of reading the account. A modal is
// right for a short question and wrong for a document.
//
// ⚠️ THE LIST BEHIND IT KEEPS ITS PLACE. Going back lands where you left off,
// forty rows down — see `useScrollRestore` in TallyBooks. Somebody checking six
// parties down a long list otherwise pays the scroll back each time, which is
// the cost that stops people opening the second one.
//
// ⚠️ EVERY FIELD TALLY GAVE US IS AVAILABLE HERE, and most start switched off.
// The sync has always pulled GST breakups, place of supply, stock lines and
// voucher classes; the statement drew a date, a number and an amount, so
// checking a GST figure against the books meant opening Tally itself. Showing
// all nineteen by default would be its own kind of useless, so the reader picks
// — and the picking is remembered.

/** A balance in the accounting convention: credit in parentheses. */
function Balance({ value, strong }) {
  if (value == null) return <span className="tally-num">—</span>;
  const n = Number(value) || 0;
  const text = n < 0 ? `(${money(Math.abs(n))})` : money(n);
  return (
    <span className={`tally-num${n < 0 ? ' tally-num-cr' : ''}${strong ? ' tally-num-strong' : ''}`}>
      {text}
    </span>
  );
}

/** ⚠️ Nil prints blank, not "0.00" — a column of zeros beside every figure
 *  defeats the glance the column exists for. */
const Money = ({ value }) => (
  <span className="tally-num">{value ? money(value) : ''}</span>
);

const Text = ({ value }) => <span>{value || '—'}</span>;

function TallyLedgerScreen({ connectionId, ledgerId, companyLabel, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');

  // ⚠️ ONE KEY FOR THE STATEMENT TABLE, NOT ONE PER LEDGER. The choice is about
  // the SHAPE of a ledger account — "I never want to see place of supply" — not
  // about one party, and keying it per ledger would mean setting the same
  // preference again on every account a person opens.
  const cols = useHiddenColumns('tally.cols.ledgerStatement');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get(
        `/api/tally/connections/${connectionId}/ledgers/${ledgerId}/statement`,
      );
      setData(res.data || null);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not read that ledger');
    } finally {
      setLoading(false);
    }
  }, [connectionId, ledgerId]);

  useEffect(() => { load(); }, [load]);

  const ledger = data?.ledger;
  const rec = data?.reconciliation;

  // ⚠️ EVERY COLUMN THE MIRROR CAN FILL, declared once as data so the picker and
  // the table are driven by ONE list. Two lists is how a column becomes
  // switchable but never rendered, or rendered with no way to switch it off.
  const ALL_COLUMNS = useMemo(() => [
    // `always` — without a date and a voucher, a row cannot be identified at all.
    {
      key: 'date', header: 'Date', always: true, render: (r) => day(r.date),
    },
    {
      key: 'voucher',
      header: 'Voucher',
      always: true,
      render: (r) => (
        <div className="tally-statement-vch">
          <span>{r.voucherType || 'Entry'}</span>
          {r.voucherNumber && <span className="tally-muted">{`#${r.voucherNumber}`}</span>}
          {/* ⚠️ MARKED, NOT HIDDEN. A cancelled voucher leaves a hole in a
              numbered series, and a hole reads as lost data to whoever is
              reconciling. The server keeps it out of the totals instead. */}
          {r.isCancelled && <StatusPill variant="danger" label="Cancelled" />}
          {r.isOptional && <StatusPill variant="warning" label="Optional" />}
        </div>
      ),
    },
    {
      key: 'counterparts',
      label: 'Particulars (the other side)',
      header: 'Particulars',
      note: 'Which accounts the entry hit',
      render: (r) => (!r.counterparts.length ? '—' : (
        <div className="tally-particulars">
          {r.counterparts.map((c) => (
            <div key={`${c.name}-${c.amount}`}>
              <span>{c.name}</span>
              <span className="tally-muted">{` ${c.isDebit ? 'Dr' : 'Cr'} ${money(c.amount)}`}</span>
            </div>
          ))}
        </div>
      )),
    },
    {
      key: 'narration',
      label: 'Narration / description',
      header: 'Narration',
      note: 'What the person typed on the voucher',
      render: (r) => (r.narration
        ? <span className="tally-statement-narration">{r.narration}</span>
        : '—'),
    },
    {
      key: 'party', label: 'Party on the voucher', header: 'Party', render: (r) => <Text value={r.partyLedgerName} />,
    },
    {
      key: 'bills',
      label: 'Bill references',
      header: 'Against bill',
      note: 'Bill-wise allocation, where the client keeps one',
      render: (r) => (r.bills.length
        ? r.bills.map((b) => b.name).join(', ')
        : '—'),
    },
    {
      key: 'debit', header: 'Debit', always: true, render: (r) => <Money value={r.debit} />,
    },
    {
      key: 'credit', header: 'Credit', always: true, render: (r) => <Money value={r.credit} />,
    },
    {
      key: 'balance',
      label: 'Running balance',
      header: 'Balance',
      render: (r) => <Balance value={r.runningBalance} />,
    },
    {
      key: 'gross',
      label: 'Voucher total',
      header: 'Voucher total',
      note: 'The whole voucher, not just this ledger’s share',
      render: (r) => <Money value={r.grossAmount} />,
    },
    {
      key: 'taxable', label: 'Taxable value', header: 'Taxable', note: 'GST vouchers only', render: (r) => <Money value={r.taxableValue} />,
    },
    { key: 'cgst', header: 'CGST', note: 'GST vouchers only', render: (r) => <Money value={r.cgst} /> },
    { key: 'sgst', header: 'SGST', note: 'GST vouchers only', render: (r) => <Money value={r.sgst} /> },
    { key: 'igst', header: 'IGST', note: 'GST vouchers only', render: (r) => <Money value={r.igst} /> },
    { key: 'cess', header: 'Cess', note: 'GST vouchers only', render: (r) => <Money value={r.cess} /> },
    {
      key: 'pos', label: 'Place of supply', header: 'Place of supply', render: (r) => <Text value={r.placeOfSupply} />,
    },
    {
      key: 'partyGstin', label: 'Party GSTIN', header: 'Party GSTIN', render: (r) => <Text value={r.partyGstin} />,
    },
    {
      key: 'voucherClass',
      label: 'Voucher class',
      header: 'Class',
      note: 'Tally’s underlying type, under whatever the firm renamed it',
      render: (r) => <Text value={r.voucherClass} />,
    },
    {
      key: 'inventory',
      label: 'Stock items',
      header: 'Stock',
      note: 'Item, quantity and rate, where the voucher carries inventory',
      render: (r) => (!r.inventory.length ? '—' : (
        <div className="tally-particulars">
          {r.inventory.map((i) => (
            <div key={`${i.item}-${i.amount}`}>
              <span>{i.item}</span>
              <span className="tally-muted">
                {` ${i.quantity}${i.unit ? ` ${i.unit}` : ''} × ${money(i.rate)}`}
                {i.hsn ? ` · HSN ${i.hsn}` : ''}
                {i.godown ? ` · ${i.godown}` : ''}
              </span>
            </div>
          ))}
        </div>
      )),
    },
    {
      key: 'guid',
      label: 'Tally GUID',
      header: 'GUID',
      note: 'How to prove a row here is a row over there',
      render: (r) => <span className="tally-mono">{r.guid || '—'}</span>,
    },
    {
      key: 'synced', label: 'Read from Tally', header: 'Read', render: (r) => dayTime(r.syncedAt),
    },
  ], []);

  const columns = useMemo(() => applyHidden(ALL_COLUMNS, cols.hidden), [ALL_COLUMNS, cols.hidden]);

  // ⚠️ FILTERED IN THE BROWSER, DELIBERATELY. The whole account is already here
  // — it has to be, or the running balance would be wrong — so a round trip to
  // narrow it would be slower than the filter and would leave the balance
  // column describing a set the reader cannot see.
  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data?.rows || [];
    return (data?.rows || []).filter((r) => [
      r.voucherType, r.voucherNumber, r.narration, r.partyLedgerName,
      r.partyGstin, r.placeOfSupply, r.voucherClass,
      ...r.counterparts.map((c) => c.name),
      ...r.bills.map((b) => b.name),
      ...r.inventory.map((i) => i.item),
    ].join(' ').toLowerCase().includes(needle));
  }, [data, q]);

  return (
    <>
      <Card>
        <div className="tally-head">
          <div>
            {/* Back before the title: it is the control somebody reaches for
                first, and on a screen reached by a click it must not be hunted
                for at the bottom of a long table. */}
            <Button variant="ghost" size="sm" onClick={onBack}>← Back to the books</Button>
            <h3 className="tally-section-title">{ledger?.name || 'Ledger'}</h3>
            <p className="tally-muted">
              {companyLabel}
              {ledger?.parent ? ` · ${ledger.parent}` : ''}
            </p>
          </div>
        </div>

        {loading && !data ? (
          <p className="tally-muted">Reading the account…</p>
        ) : error ? (
          <p className="tally-error">{error}</p>
        ) : !data ? null : (
          <>
            <div className="tally-statement-id">
              {ledger.gstin && <span className="tally-chip">{ledger.gstin}</span>}
              {ledger.pan && <span className="tally-chip">{ledger.pan}</span>}
              {ledger.stateName && <span className="tally-chip">{ledger.stateName}</span>}
              {ledger.matchStatus === 'confirmed' && (
                <StatusPill variant="success" label="Matched to a client" />
              )}
            </div>

            {/* Contact details Tally holds. Shown only when there is something
                to show — an empty row of dashes is furniture. */}
            {(ledger.address || ledger.email || ledger.phone) && (
              <p className="tally-muted">
                {[ledger.address, ledger.email, ledger.phone].filter(Boolean).join(' · ')}
              </p>
            )}

            <div className="tally-position-totals">
              <div>
                <span className="tally-kpi-label">Opening</span>
                <Balance value={ledger.openingBalance} strong />
              </div>
              <div>
                <span className="tally-kpi-label">Debits</span>
                <span className="tally-num tally-num-strong">{money(data.totals.debit)}</span>
              </div>
              <div>
                <span className="tally-kpi-label">Credits</span>
                <span className="tally-num tally-num-strong">{money(data.totals.credit)}</span>
              </div>
              <div>
                <span className="tally-kpi-label">Closing</span>
                <Balance value={ledger.closingBalance} strong />
              </div>
            </div>

            {/* ⚠️ THE BRIDGE IS ALWAYS DRAWN, not only when it breaks. It is the
                whole reason somebody opened this: the ledger list showed an
                opening and a closing figure and nothing that joined them. */}
            {rec && (
              <p className={rec.reconciles ? 'tally-muted' : 'tally-bridge-off'}>
                <span className="tally-num">{money(rec.opening)}</span>
                {' opening '}
                {rec.movement < 0 ? '−' : '+'}
                <span className="tally-num">{money(Math.abs(rec.movement))}</span>
                {` moved by ${data.totals.count - data.totals.excluded} entries = `}
                <span className="tally-num">{money(rec.derivedClosing)}</span>
                {rec.reconciles
                  ? ', which is the closing balance Tally reports.'
                  : `, against the ${money(rec.reportedClosing)} Tally reports.`}
              </p>
            )}

            {rec && !rec.reconciles && (
              <p className="tally-warn">
                <strong>This account is not complete.</strong>{' '}
                {`${money(Math.abs(rec.difference))} of movement is missing between the entries below and the closing balance Tally holds. `}
                {rec.truncated
                  ? 'This ledger has more entries than one screen holds, so the earliest are not shown.'
                  : 'That usually means the historical backfill has not finished mirroring this ledger’s older vouchers yet.'}
              </p>
            )}
          </>
        )}
      </Card>

      {data && (
        <Card>
          <div className="tally-head">
            <div className="tally-statement-search">
              <Input
                label="Find in this account"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Voucher number, narration, account, stock item…"
              />
            </div>
            <div className="tally-actions">
              <ColumnPicker
                columns={ALL_COLUMNS}
                hidden={cols.hidden}
                onToggle={cols.toggle}
                onShowAll={cols.showAll}
              />
            </div>
          </div>

          {!data.rows.length ? (
            <EmptyState
              icon="📄"
              title="No entries against this ledger"
              description="Nothing mirrored so far has touched this account. If the historical backfill is still running, older vouchers may not have arrived yet."
            />
          ) : !rows.length ? (
            <EmptyState
              icon="🔍"
              title="Nothing in this account matches that"
              description="Clear the search to see every entry again."
            />
          ) : (
            <>
              {/* ⚠️ THE TABLE SCROLLS SIDEWAYS, THE PAGE DOES NOT. Nineteen
                  columns is wider than any screen, and a body that scrolls
                  horizontally takes the sidebar and the header with it. */}
              <div className="tally-wide-table">
                <Table columns={columns} data={rows} keyField="id" />
              </div>

              <p className="tally-muted">
                {q
                  ? `${rows.length} of ${data.totals.count} entries match.`
                  : `${data.totals.count} entr${data.totals.count === 1 ? 'y' : 'ies'}`}
                {!q && data.totals.excluded > 0 && `, of which ${data.totals.excluded} cancelled or optional and not counted`}
                {'. Every figure is Tally’s own, as at the last sync — nothing here is recomputed by us.'}
              </p>
            </>
          )}
        </Card>
      )}
    </>
  );
}

export default TallyLedgerScreen;
